import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState("checking");

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return setState("failed");
    let tries = 0;
    const tick = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") return setState("paid");
        if (["failed", "expired"].includes(data.status)) return setState("failed");
      } catch { /* keep polling */ }
      if (tries >= 10) return setState("pending");
      setTimeout(tick, 2000);
    };
    tick();
  }, [params]);

  return (
    <div className="min-h-screen flex items-center justify-center px-5">
      <div data-testid="payment-result" className="bg-card border border-border rounded-2xl p-10 max-w-md text-center">
        {state === "checking" && (
          <>
            <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
            <h1 className="font-display text-2xl font-extrabold mt-5">Confirming your payment…</h1>
            <p className="text-sm text-muted-foreground mt-2">This takes just a moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="w-10 h-10 text-brand mx-auto" />
            <h1 data-testid="payment-success-title" className="font-display text-2xl font-extrabold mt-5">Subscription active</h1>
            <p className="text-sm text-muted-foreground mt-2">Your new plan and quote allowance are live.</p>
            <Button data-testid="payment-go-billing" onClick={() => navigate("/billing")} className="mt-6 font-semibold">View my plan</Button>
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 className="w-8 h-8 text-muted-foreground mx-auto" />
            <h1 className="font-display text-2xl font-extrabold mt-5">Still processing</h1>
            <p className="text-sm text-muted-foreground mt-2">
              Your payment is being confirmed. Refresh the billing page in a minute, or email admin@matiqs.net.
            </p>
            <Button variant="outline" onClick={() => navigate("/billing")} className="mt-6 font-semibold">Back to billing</Button>
          </>
        )}
        {state === "failed" && (
          <>
            <XCircle className="w-10 h-10 text-destructive mx-auto" />
            <h1 className="font-display text-2xl font-extrabold mt-5">Payment not completed</h1>
            <p className="text-sm text-muted-foreground mt-2">Nothing has been charged. You can try again any time.</p>
            <Button onClick={() => navigate("/billing")} className="mt-6 font-semibold">Back to billing</Button>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen flex items-center justify-center px-5">
      <div data-testid="payment-cancelled" className="bg-card border border-border rounded-2xl p-10 max-w-md text-center">
        <XCircle className="w-10 h-10 text-muted-foreground mx-auto" />
        <h1 className="font-display text-2xl font-extrabold mt-5">Checkout cancelled</h1>
        <p className="text-sm text-muted-foreground mt-2">No payment was taken. Your current plan is unchanged.</p>
        <Button onClick={() => navigate("/billing")} className="mt-6 font-semibold">Back to billing</Button>
      </div>
    </div>
  );
}
